/**
 * The furnace panel — "pop where is ore going to bars".
 *
 * Clicking a built furnace opens this: your raw ore on the left, the bar it makes
 * on the right, an amount to load, and — while it burns — how many bars are done,
 * how long until the next one, and a Collect button. It is the visible middle of
 * the chain (vein → raw ore → BAR → gear), and the only place metal enters the
 * game.
 *
 * Nothing here decides anything: the amounts, the timings and the "can I afford
 * it" are all the server's answer (`/smelting`), re-read after every action. The
 * countdown between reads is interpolated locally from `remainingMs` purely so
 * the number moves — when it hits zero the panel ASKS rather than promoting the
 * bars itself, the same rule construction timers follow.
 *
 * No dimming backdrop: this is an in-world dialog, so the click-catcher is
 * invisible and the farm stays lit behind it (the owner asked for the shade gone).
 */
import { el, toast } from '../../dom';
import { ApiError } from '../../../core/network/api';
import { playSfx } from '../../audio/audio';
import { fieldApi, smeltingApi, type SmeltStatus, type SmeltOre, type SmeltJob } from '../../game-api';
import { dispatchAction, isDropped } from '../../action/dispatch';
import { oreBarIcon, oreChunkIcon, ORES, type OreType } from '../../map/ores';
import { closePlanter, guard, openOverlay, refreshBag, setFarm, type FarmCtx } from './context';
import { paintPlots } from './plots';
import { fmtCountdown } from './construction';
import { richText } from '../../ui/icons';

/** How many bars the player has dialled up per ore, kept across re-renders. */
const batch = new Map<string, number>();

const pxImg = (src: string, cls = 'w-8 h-8') =>
  el('img', `${cls} object-contain`, { src, style: 'image-rendering:pixelated' });

/**
 * The tier the legacy unsorted pile is allowed to buy — copper only.
 *
 * Mirrors COPPER_TIER in backend smelting.service.ts. This client used to add
 * `unsortedOre` to EVERY metal, so a player holding a pile of old ore was shown a
 * live Smelt button for gold and mythril that the server then refused with a 400:
 * "Unsorted ore only smelts copper". The rule has to be in both places or the UI
 * promises something the server will not honour.
 */
const UNSORTED_TIER = 1;

/** Raw ore the player can actually put in: typed ore, plus the legacy pile for copper. */
export function feedstock(st: SmeltStatus, o: SmeltOre): number {
  return o.raw + (o.pickaxeTier <= UNSORTED_TIER ? st.unsortedOre : 0);
}

/**
 * Is there anything to smelt of this metal at all?
 *
 * OWNER (2026-08-16): "in furnace, do not show any possible items, if you don't
 * have ore." Every metal in the game had a row whatever you were carrying, so the
 * furnace opened as a wall of six entries with five of them inert and labelled
 * "no ore" — a catalogue of things you cannot do, with the one you CAN do hidden
 * among them. A metal you have no ore for is simply not offered now.
 *
 * Deliberately NOT gated on coal: no coal with ore in hand is a real, actionable
 * state ("go burn some wood"), and hiding the row would leave the player with an
 * empty furnace and no idea why. That case keeps its explanatory label.
 */
export const hasFeedstock = (st: SmeltStatus, o: SmeltOre): boolean =>
  feedstock(st, o) >= o.orePerBar;

/** The metals worth showing: the ones the player can actually load. */
export const loadableOres = (st: SmeltStatus): SmeltOre[] => st.ores.filter((o) => hasFeedstock(st, o));

/** Most bars this ore could make right now, bounded by ore, coal and the furnace. */
export function maxBars(st: SmeltStatus, o: SmeltOre): number {
  return Math.max(0, Math.min(
    st.maxBatch,
    Math.floor(feedstock(st, o) / o.orePerBar),
    o.coalPerBar > 0 ? Math.floor(st.coal / o.coalPerBar) : st.maxBatch,
  ));
}

/**
 * Where the job has got to, RIGHT NOW — interpolated between server reads.
 *
 * OWNER (2026-08-16): "in furnace show live tracker of the melting process (I
 * don't see)". The panel had the numbers but nothing moved: it re-rendered only
 * when a bar FINISHED (scheduleRefresh at nextBarMs), so on a 30s bar the
 * countdown and the progress bar froze for thirty seconds at a stretch. Correct
 * at the moment of render, dead immediately after — which reads as a furnace that
 * is not doing anything.
 *
 * Interpolating locally is the same trick the farm already uses for crop timers
 * (liveRemaining in context.ts): the server stays the authority on what is READY,
 * and the client only animates the gap between its answers. Nothing here can
 * invent a bar — `done` is bars the server confirmed plus the FRACTION of the one
 * in the fire, so the count never rounds up past what has actually been smelted.
 */
export function smeltProgress(j: SmeltJob, elapsedMs: number): {
  nextBarMs: number; remainingMs: number; fraction: number; done: number;
} {
  if (j.complete) {
    return { nextBarMs: 0, remainingMs: 0, fraction: 1, done: j.qty };
  }
  const nextBarMs = Math.max(0, j.nextBarMs - elapsedMs);
  const remainingMs = Math.max(0, j.remainingMs - elapsedMs);
  // How far through the CURRENT bar the fire has got, 0..1.
  const perBar = j.perBarMs > 0 ? j.perBarMs : 1;
  const inFire = j.pending > 0 ? Math.min(1, Math.max(0, 1 - nextBarMs / perBar)) : 0;
  const banked = j.collected + j.ready;
  const done = Math.min(j.qty, banked + inFire);
  return { nextBarMs, remainingMs, fraction: j.qty > 0 ? done / j.qty : 0, done };
}

export function openSmelting(c: FarmCtx): void {
  guard('ui.smelting', async () => {
    const st = await smeltingApi.status();
    render(c, st);
  });
}

function render(c: FarmCtx, st: SmeltStatus): void {
  const card = el('div',
    'bg-surface border border-border rounded-lg p-4 w-[430px] max-w-[92vw] max-h-[86vh] overflow-y-auto shadow-lift sheet-card pointer-events-auto',
    {},
    el('div', 'flex justify-between items-center mb-1', {},
      el('h3', 'font-heading text-gold flex items-center gap-2', {},
        pxImg('/sprites/buildings/furnace_lit.png', 'w-6 h-8'), 'Furnace'),
      el('button', 'tb-close-x text-text-dim hover:text-text text-lg', { onClick: () => closePlanter(c) }, '×')),
    el('div', 'text-[11px] text-text-dim mb-3', {},
      'Raw ore in, bars out. Bars are what every metal tool, weapon and piece of armour is made of.'));

  if (!st.hasFurnace) {
    card.append(el('div', 'text-sm text-error mb-2 inline-flex items-center gap-1', {}, ...richText('Place a Furnace on your farm first (🏗️ Build).')));
  }

  // ── What is burning right now ───────────────────────────────────────────────
  if (st.job) {
    const j = st.job;
    const ore = ORES[j.type as OreType];
    const fill = el('div', 'h-full transition-[width] duration-300 ease-linear', {
      style: `width:0%;background:${ore?.color ?? '#d9a13a'}`,
    });
    const bar = el('div', 'h-2 rounded bg-black/40 overflow-hidden mb-2', {}, fill);
    const lineEl = el('div', 'text-[11px] text-text-dim font-mono mb-2', {}, '');
    const countEl = el('div', 'text-[11px] text-text-dim font-mono', {}, '');
    const box = el('div', 'rounded border border-gold/50 p-3 mb-3', {},
      el('div', 'flex items-center gap-3 mb-2', {},
        pxImg(oreBarIcon(ore ?? ORES.copper)),
        el('div', 'flex-1', {},
          el('div', 'text-sm text-gold', {}, `Smelting ${j.name}`),
          countEl)),
      bar,
      lineEl);
    const actions = el('div', 'flex gap-2', {},
      el('button',
        `flex-1 px-2.5 py-1.5 rounded border text-sm ${j.ready > 0 ? 'border-gold text-gold hover:bg-gold/10' : 'border-border text-text-dim opacity-50 cursor-not-allowed'}`,
        { onClick: j.ready > 0 ? () => doCollect(c) : undefined },
        j.ready > 0 ? `Collect ${j.ready} bar${j.ready === 1 ? '' : 's'}` : 'Nothing ready yet'),
      el('button', 'px-2.5 py-1.5 rounded border border-error/70 text-error hover:bg-error/10 text-sm',
        { title: 'Finished bars are kept; ore for bars that have not started comes back. The one in the fire is lost.',
          onClick: () => doCancel(c) }, 'Douse'));
    box.append(actions);
    card.append(box);
    // Two clocks, deliberately. The SERVER is re-asked when a bar is due (it owns
    // what is ready); the local ticker only animates the gap in between, so the
    // player can see the fire working instead of staring at a frozen number.
    startTicker(c, j, fill, lineEl, countEl);
    scheduleRefresh(c, j.complete ? 0 : j.nextBarMs);
  }

  // ── Load the furnace ────────────────────────────────────────────────────────
  card.append(el('div', 'text-[11px] uppercase tracking-wide text-text-dim mb-1 flex justify-between', {},
    el('span', '', {}, 'Ore'),
    el('span', 'font-mono normal-case', {}, `coal ${st.coal}${st.unsortedOre ? ` · unsorted ore ${st.unsortedOre}` : ''}`)));

  const loadable = loadableOres(st);
  if (!loadable.length) {
    // The furnace is not broken, it is empty — say which, and say what fixes it.
    card.append(el('div', 'text-[12px] text-text-dim p-2 mb-1.5 rounded border border-border', {},
      st.unsortedOre > 0
        ? 'Only unsorted ore in the bag — it smelts copper once you have enough. Mine a vein for anything better.'
        : 'No ore yet — swing a pickaxe at a vein and bring the ore back here.'));
  }
  for (const o of loadable) {
    const cap = maxBars(st, o);
    const want = Math.min(Math.max(1, batch.get(o.type) ?? 1), Math.max(1, cap));
    batch.set(o.type, want);
    const canStart = st.hasFurnace && !st.job && cap >= 1;

    const step = (delta: number) => el('button',
      'w-7 h-7 rounded border border-border text-text-dim hover:text-gold hover:border-gold leading-none',
      { onClick: () => { batch.set(o.type, Math.min(cap, Math.max(1, want + delta))); render(c, st); } },
      delta > 0 ? '+' : '−');

    const row = el('div', 'flex items-center gap-2 p-2 mb-1.5 rounded border border-border', {},
      pxImg(oreChunkIcon(ORES[o.type as OreType] ?? ORES.copper)),
      el('div', 'flex-1 min-w-0', {},
        el('div', 'text-sm', { style: `color:${o.color}` }, o.name),
        el('div', 'text-[11px] text-text-dim font-mono', {},
          `${o.raw} raw · ${o.bars} bars · ${o.orePerBar} ore + ${o.coalPerBar} coal → 1 bar / ${Math.round(o.msPerBar / 1000)}s`)),
      pxImg(oreBarIcon(ORES[o.type as OreType] ?? ORES.copper), 'w-6 h-6'));

    if (canStart) {
      row.append(step(-1), el('span', 'w-6 text-center text-sm font-mono', {}, String(want)), step(+1),
        el('button', 'px-2.5 py-1 rounded border border-gold text-gold hover:bg-gold/10 text-sm',
          { onClick: () => doStart(c, o.type, want) }, 'Smelt'));
    } else {
      // Say WHY it is not offered — an inert row with no explanation is the thing
      // players read as a broken button.
      // 'no ore' is gone from this list on purpose: a metal with no ore is no
      // longer rendered at all (loadableOres), so the only reasons left are ones
      // the player can act on without leaving the panel.
      const why = st.job ? 'furnace busy'
        : !st.hasFurnace ? 'no furnace'
        : 'no coal';
      row.append(el('span', 'text-[11px] text-text-dim italic', {}, why));
    }
    card.append(row);
  }

  // Invisible click-catcher: it swallows a stray click so it doesn't land on the
  // farm behind, and paints NOTHING (no dimming — see the file header).
  const wrap = el('div', 'fixed inset-0 z-[65] flex items-center justify-center',
    { onClick: (e) => { if (e.target === wrap) closePlanter(c); } }, card);
  const first = !c.overlay;
  openOverlay(c, wrap);
  mounted = wrap;                 // the node the refresh timer is allowed to replace
  if (first) playSfx('ui_open');
}

/**
 * Re-read the farm and repaint, so the furnace on the map switches between cold
 * and burning the moment you light or douse it — the `smelting` flag rides on the
 * farm snapshot (see backend field.service.ts), not on this panel's own status.
 */
async function repaintFurnace(c: FarmCtx): Promise<void> {
  try { setFarm(c, await fieldApi.get()); paintPlots(c); } catch { /* the next repaint will catch up */ }
}

/**
 * Pending re-read timer — one at a time, and it may only ever repaint THIS panel.
 *
 * The guard used to be "some overlay is mounted", which is a different claim: a
 * player who lit a load, pressed Escape and opened Crafting had the crafting
 * panel torn down and replaced by the furnace mid-interaction when the timer
 * fired. So the wrap node this panel mounted is remembered and compared by
 * identity — anything else on screen means the furnace has no business there.
 */
let refreshTimer = 0;
let tickTimer = 0;
let mounted: HTMLElement | null = null;

/**
 * Animate the job between server reads.
 *
 * Updates three text/style properties in place rather than re-rendering the card:
 * a full repaint every half second would rebuild the ore rows underneath, throwing
 * away the batch spinner the player is in the middle of dialling and flickering
 * the whole panel. Stops itself the moment the panel is gone, so a closed furnace
 * leaves no timer running.
 */
function startTicker(
  c: FarmCtx,
  j: SmeltJob,
  fill: HTMLElement,
  lineEl: HTMLElement,
  countEl: HTMLElement,
): void {
  window.clearInterval(tickTimer);
  const startedAt = Date.now();
  const paint = () => {
    const live = !!mounted && c.overlay === mounted && mounted.isConnected;
    if (!live) { window.clearInterval(tickTimer); tickTimer = 0; mounted = null; return; }
    const p = smeltProgress(j, Date.now() - startedAt);
    fill.style.width = `${Math.round(p.fraction * 100)}%`;
    countEl.textContent = `${j.collected + j.ready}/${j.qty} bars ready · ${j.pending} still in the fire`;
    lineEl.textContent = j.complete
      ? 'All done — the fire is out.'
      : `Next bar in ${fmtCountdown(p.nextBarMs)} · all ${j.qty} in ${fmtCountdown(p.remainingMs)}`;
  };
  paint();                                  // no blank frame before the first tick
  if (!j.complete) tickTimer = window.setInterval(paint, 500);
}
function scheduleRefresh(c: FarmCtx, ms: number): void {
  window.clearTimeout(refreshTimer);
  if (ms <= 0) return;
  const live = () => !!mounted && c.overlay === mounted && mounted.isConnected;
  refreshTimer = window.setTimeout(() => {
    if (!live()) { mounted = null; return; }
    void smeltingApi.status().then((st) => { if (live()) render(c, st); }).catch(() => {});
  }, Math.min(ms + 250, 60_000));
}

async function doStart(c: FarmCtx, type: string, bars: number): Promise<void> {
  try {
    const r = await dispatchAction('smelt.start', (id) => smeltingApi.start(type, bars, id), { key: `${type}:${bars}` });
    playSfx('ui_open');
    toast(`The furnace is lit — ${bars} ${type} bar${bars === 1 ? '' : 's'} on the way`);
    render(c, r.status);
    refreshBag(c);
    await c.game.refreshHud();
    await repaintFurnace(c);
  } catch (e) {
    if (isDropped(e)) return;      // the funnel refused a repeat — nothing was attempted
    toast(e instanceof ApiError ? e.message : 'Could not light the furnace', 'error');
  }
}

async function doCollect(c: FarmCtx): Promise<void> {
  try {
    const r = await dispatchAction('smelt.collect', (id) => smeltingApi.collect(id));
    playSfx('craft_complete');
    toast(`Collected ${r.collected} ${r.type} bar${r.collected === 1 ? '' : 's'}`);
    render(c, r.status);
    refreshBag(c);
    await c.game.refreshHud();
    await repaintFurnace(c);
  } catch (e) {
    if (isDropped(e)) return;      // the funnel refused a repeat — nothing was attempted
    toast(e instanceof ApiError ? e.message : 'Collect failed', 'error');
  }
}

async function doCancel(c: FarmCtx): Promise<void> {
  try {
    const r = await dispatchAction('smelt.cancel', (id) => smeltingApi.cancel(id));
    toast(r.refundedOre ? `Fire out — ${r.refundedOre} ore back` : 'Fire out');
    render(c, r.status);
    refreshBag(c);
    await c.game.refreshHud();
    await repaintFurnace(c);
  } catch (e) {
    if (isDropped(e)) return;      // the funnel refused a repeat — nothing was attempted
    toast(e instanceof ApiError ? e.message : 'Could not douse the fire', 'error');
  }
}
